import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { QuestionnaireDefinitionData, PopupButton } from '../../types/questionnaire.types';

interface QuestionnairePreviewContentProps {
  definition: QuestionnaireDefinitionData;
  title: string;
}

export const QuestionnairePreviewContent: React.FC<QuestionnairePreviewContentProps> = ({
  definition,
  title,
}) => {
  const [currentSectionIndex, setCurrentSectionIndex] = React.useState(0);
  const [isContentScrollable, setIsContentScrollable] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [openPopupId, setOpenPopupId] = React.useState<string | null>(null);

  // Reset to first section when definition changes
  React.useEffect(() => {
    setCurrentSectionIndex(0);
    setOpenPopupId(null);
  }, [definition]);

  // Detect if content is scrollable
  React.useEffect(() => {
    const checkScrollable = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight } = contentRef.current;
        setIsContentScrollable(scrollHeight > clientHeight + 10);
      }
    };

    const timer = setTimeout(checkScrollable, 100);
    window.addEventListener('resize', checkScrollable);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkScrollable);
    };
  }, [currentSectionIndex, definition]);

  const handleNextSection = () => {
    if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  const handleOpenPopup = (popupId: string) => {
    setOpenPopupId(popupId);
  };

  const handleClosePopup = () => {
    setOpenPopupId(null);
  };

  const handlePopupButtonClick = (button: PopupButton) => {
    switch (button.action) {
      case 'close-popup':
        handleClosePopup();
        break;
      case 'next':
        handleClosePopup();
        handleNextSection();
        break;
      case 'submit':
        handleClosePopup();
        alert('Submit action in preview mode');
        break;
    }
  };

  if (!definition || !definition.sections || definition.sections.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h5"
          align="center"
          sx={{ color: '#2c7a7b', fontWeight: 600, mb: 3, fontFamily: '"Open Sans", sans-serif' }}
        >
          {title}
        </Typography>
        <Typography color="textSecondary" align="center" sx={{ fontFamily: '"Open Sans", sans-serif' }}>
          No sections to preview
        </Typography>
      </Box>
    );
  }

  const totalSections = definition.sections.length;
  const currentSection = definition.sections[currentSectionIndex];

  if (!currentSection) {
    return null;
  }

  const renderField = (field: any) => {
    const key = `${field.name}`;
    const isRequired = field.required;
    const displayLabel = field.label;

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, fontFamily: '"Open Sans", sans-serif' }}
            >
              {displayLabel}{isRequired && '*'}
            </Typography>
            <TextField
              fullWidth
              placeholder={field.placeholder}
              disabled
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#f7fafc',
                  '& fieldset': { borderColor: '#B4D8D6 !important' },
                  '&:hover fieldset': { borderColor: '#B4D8D6 !important' },
                  '&.Mui-disabled fieldset': { borderColor: '#B4D8D6 !important' },
                },
                '& .MuiInputBase-input': {
                  padding: '14px 16px',
                  '&::placeholder': { color: '#B7B7B7', opacity: 1 },
                },
              }}
            />
          </Box>
        );

      case 'textarea':
        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, fontFamily: '"Open Sans", sans-serif' }}
            >
              {displayLabel}{isRequired && '*'}
            </Typography>
            <TextField
              fullWidth
              placeholder={field.placeholder}
              multiline
              rows={3}
              disabled
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#f7fafc',
                  '& fieldset': { borderColor: '#B4D8D6 !important' },
                  '&:hover fieldset': { borderColor: '#B4D8D6 !important' },
                  '&.Mui-disabled fieldset': { borderColor: '#B4D8D6 !important' },
                },
                '& .MuiInputBase-input': {
                  '&::placeholder': { color: '#B7B7B7', opacity: 1 },
                },
                // Tailwind preflight forces `line-height: inherit` on <textarea>; pin it
                // so the text stays inside the box.
                '& .MuiInputBase-inputMultiline': { lineHeight: 1.5 },
              }}
            />
          </Box>
        );

      case 'date':
        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, fontFamily: '"Open Sans", sans-serif' }}
            >
              {displayLabel}{isRequired && '*'}
            </Typography>
            <TextField
              fullWidth
              type="date"
              disabled
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#f7fafc',
                  '& fieldset': { borderColor: '#B4D8D6 !important' },
                  '&:hover fieldset': { borderColor: '#B4D8D6 !important' },
                  '&.Mui-disabled fieldset': { borderColor: '#B4D8D6 !important' },
                },
                '& .MuiInputBase-input': {
                  padding: '14px 16px',
                  '&::-webkit-datetime-edit': { color: 'transparent' },
                  '&::-webkit-datetime-edit-fields-wrapper': { color: 'transparent' },
                  '&::before': { content: '"DD/MM/YYYY"', color: '#B7B7B7', position: 'absolute', pointerEvents: 'none' },
                },
              }}
            />
          </Box>
        );

      case 'number':
        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, fontFamily: '"Open Sans", sans-serif' }}
            >
              {displayLabel}{isRequired && '*'}
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder={field.placeholder}
              disabled
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#f7fafc',
                  '& fieldset': { borderColor: '#B4D8D6 !important' },
                  '&:hover fieldset': { borderColor: '#B4D8D6 !important' },
                  '&.Mui-disabled fieldset': { borderColor: '#B4D8D6 !important' },
                },
                '& .MuiInputBase-input': {
                  padding: '14px 16px',
                  '&::placeholder': { color: '#B7B7B7', opacity: 1 },
                },
              }}
            />
          </Box>
        );

      case 'radio':
        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, fontFamily: '"Open Sans", sans-serif' }}
            >
              {displayLabel}{isRequired && '*'}
            </Typography>
            <RadioGroup>
              {field.options?.map((option: any) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio disabled sx={{ color: '#2c7a7b' }} />}
                  label={option.label}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          </Box>
        );

      case 'checkbox':
      case 'checkboxes':
        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, fontFamily: '"Open Sans", sans-serif' }}
            >
              {displayLabel}{isRequired && '*'}
            </Typography>
            <FormGroup>
              {field.options?.map((option: any) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Checkbox disabled sx={{ color: '#2c7a7b' }} />}
                  label={option.label}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </FormGroup>
          </Box>
        );

      case 'select':
      case 'multiselect':
        const isMultiple = field.type === 'multiselect';

        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, fontFamily: '"Open Sans", sans-serif' }}
            >
              {displayLabel}{isRequired && '*'}
            </Typography>
            <FormControl fullWidth>
              <Select
                multiple={isMultiple}
                value={isMultiple ? [] : ''}
                disabled
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                    return field.placeholder ? <span style={{ color: '#B7B7B7' }}>{field.placeholder}</span> : '';
                  }
                  return selected;
                }}
                sx={{
                  borderRadius: '12px',
                  backgroundColor: '#f7fafc',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#B4D8D6 !important' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B4D8D6 !important' },
                  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: '#B4D8D6 !important' },
                  '& .MuiSelect-select': { padding: '14px 16px' },
                }}
              >
                {field.options?.map((option: any) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 'button-group':
        const buttonHeight = (field.buttonHeight || 'medium') as 'small' | 'medium' | 'large';
        const heightStyles: Record<'small' | 'medium' | 'large', { padding: string; fontSize: string }> = {
          small: { padding: '6px 12px', fontSize: '0.85rem' },
          medium: { padding: '14px 20px', fontSize: '0.95rem' },
          large: { padding: '18px 24px', fontSize: '1rem' },
        };
        const currentHeightStyle = heightStyles[buttonHeight];

        return (
          <Box key={key} sx={{ mb: 3 }}>
            {(field as any).htmlContent && (field as any).htmlContent.trim() !== '' && (field as any).htmlContent !== '<p><br></p>' ? (
              <Box
                sx={{
                  mb: 1.5, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.5,
                  fontFamily: '"Open Sans", sans-serif',
                  '& p': { mb: 1, mt: 0 },
                  '& p:last-child': { mb: 0 },
                  '& ul': { mb: 1, mt: 0.5, pl: 2.5, listStyleType: 'disc', display: 'block', marginLeft: 0 },
                  '& ol': { mb: 1, mt: 0.5, pl: 2.5, listStyleType: 'decimal', display: 'block', marginLeft: 0 },
                  '& li': { mb: 0.5, display: 'list-item' },
                }}
                dangerouslySetInnerHTML={{ __html: (field as any).htmlContent + (isRequired ? '<span style="color: red;">*</span>' : '') }}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{ mb: 1.5, color: '#3A7380', fontSize: '0.95rem', fontWeight: 500, fontFamily: '"Open Sans", sans-serif' }}
              >
                {displayLabel}{isRequired && '*'}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {field.options?.map((option: any) => (
                <Button
                  key={option.value}
                  disabled
                  sx={{
                    flex: 1, borderRadius: '12px', padding: currentHeightStyle.padding,
                    textTransform: 'none', fontSize: currentHeightStyle.fontSize, fontWeight: 500,
                    fontFamily: '"Open Sans", sans-serif', whiteSpace: 'nowrap',
                    backgroundColor: 'white', color: '#3A7380', border: '1px solid #B4D8D6',
                    '&.Mui-disabled': { backgroundColor: 'white', color: '#3A7380', borderColor: '#B4D8D6' },
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
          </Box>
        );

      case 'button':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 2, gap: 2 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#3A7380', color: 'white', borderRadius: '10px',
                minWidth: '120px', height: '40px', paddingX: '20px', textTransform: 'none',
                fontSize: '1rem', fontWeight: 600, letterSpacing: '0.05em', boxShadow: 'none',
                fontFamily: '"Open Sans", sans-serif', whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: '#2e5c66', boxShadow: 'none' },
              }}
              onClick={() => {
                if (field.action === 'open-popup' && field.popupId) {
                  handleOpenPopup(field.popupId);
                } else if (field.action === 'submit') {
                  alert('Submit action in preview mode');
                } else {
                  handleNextSection();
                }
              }}
            >
              {field.label}
            </Button>
            <Typography
              variant="caption"
              align="center"
              sx={{ display: 'block', color: '#3A7380', fontSize: '14px', fontWeight: 400, fontFamily: '"Open Sans", sans-serif' }}
            >
              {field.helperText}
            </Typography>
          </Box>
        );

      case 'content':
        const areaType = (field as any).areaType || 'box';
        const htmlContent = (field as any).content || '';

        if (areaType === 'box') {
          return (
            <Box
              key={key}
              sx={{
                backgroundColor: 'white', borderRadius: '15px', p: { xs: 2.5, md: 3 }, mb: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #E0E0E0',
              }}
            >
              <Box
                sx={{
                  color: '#5A7380', fontSize: '0.95rem', lineHeight: 1.3, fontFamily: '"Open Sans", sans-serif',
                  '& p': { mb: 1, mt: 0 },
                  '& p:last-child': { mb: 0 },
                  '& .ql-align-center': { textAlign: 'center' },
                  '& .ql-align-right': { textAlign: 'right' },
                  '& .ql-align-justify': { textAlign: 'justify' },
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </Box>
          );
        } else {
          return (
            <Box key={key} sx={{ mb: 3 }}>
              {htmlContent && (
                <Box
                  sx={{
                    color: '#5A7380', fontSize: '0.9rem', lineHeight: 1.5, fontFamily: '"Open Sans", sans-serif', mb: 2,
                    '& p': { mb: 1 },
                    '& .ql-align-center': { textAlign: 'center' },
                    '& .ql-align-right': { textAlign: 'right' },
                    '& .ql-align-justify': { textAlign: 'justify' },
                  }}
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
              )}
            </Box>
          );
        }

      case 'line':
        const nestedFields = (field as any).nestedFields || [];
        if (nestedFields.length === 0) {
          return null;
        }

        return (
          <Box
            key={key}
            sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}
          >
            {nestedFields.map((nestedField: any) => (
              <Box key={nestedField.name} sx={{ flex: '1 1 0', minWidth: '200px' }}>
                {renderField(nestedField)}
              </Box>
            ))}
          </Box>
        );

      case 'container':
        const containerNestedFields = (field as any).nestedFields || [];
        if (containerNestedFields.length === 0) {
          return null;
        }

        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Box
              sx={{
                border: '1px solid #E5F0F1', borderRadius: '12px', backgroundColor: 'white',
                boxShadow: '0px 4px 4px 0px rgba(58, 115, 128, 0.2)', p: 1.5,
              }}
            >
              {displayLabel && (
                <Typography
                  sx={{
                    color: '#3A7380', fontWeight: 700, fontSize: '20px', fontFamily: '"Open Sans", sans-serif',
                    lineHeight: '100%', letterSpacing: '0%', mb: 1.5,
                  }}
                >
                  {displayLabel}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {containerNestedFields.map((nestedField: any) => {
                  if (nestedField.type === 'line') {
                    const lineNestedFields = nestedField.nestedFields || [];
                    if (lineNestedFields.length === 0) {
                      return null;
                    }
                    return (
                      <Box
                        key={nestedField.name}
                        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}
                      >
                        {lineNestedFields.map((lineField: any) => (
                          <Box
                            key={lineField.name}
                            sx={{
                              flex: '1 1 0', minWidth: '200px',
                              '& label': { fontFamily: '"Open Sans", sans-serif', fontWeight: 400, fontSize: '20px', lineHeight: '100%', letterSpacing: '0%', color: '#3A7380', mb: 0.5, display: 'block' },
                              '& .MuiTextField-root': { mb: 0 },
                              '& .MuiOutlinedInput-root': { height: '40px', minHeight: '40px' },
                              '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                            }}
                          >
                            {renderField(lineField)}
                          </Box>
                        ))}
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={nestedField.name}
                      sx={{
                        '& > .MuiBox-root': { mb: 0 },
                        '& label': { fontFamily: '"Open Sans", sans-serif', fontWeight: 400, fontSize: '20px', lineHeight: '100%', letterSpacing: '0%', color: '#3A7380', mb: 0.5, display: 'block' },
                        '& .MuiTextField-root': { mb: 1 },
                        '& .MuiOutlinedInput-root': { height: '40px', minHeight: '40px' },
                        '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                      }}
                    >
                      {renderField(nestedField)}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  const buttonFields = currentSection.fields.filter((field) => field.type === 'button');

  return (
    <>
      {/* Scrollable Content Area */}
      <Box
        ref={contentRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 3,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#3A7380', borderRadius: '10px', border: '2px solid white',
            '&:hover': { backgroundColor: '#2e5c66' },
          },
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{
            color: '#2c7a7b', fontWeight: 600,
            mb: currentSection.description ? 2 : 4,
            fontSize: '1.75rem', fontFamily: '"Open Sans", sans-serif',
          }}
        >
          {currentSection.title}
        </Typography>

        {currentSection.description && (
          <Typography
            variant="body1"
            align="center"
            sx={{ color: '#5A7380', mb: 4, fontFamily: '"Open Sans", sans-serif' }}
          >
            {currentSection.description}
          </Typography>
        )}

        <Box>
          {currentSection.fields
            .filter((field) => field.type !== 'button')
            .map((field) => renderField(field))}
        </Box>
      </Box>

      {buttonFields.length > 0 && (
        <Box
          sx={{
            backgroundColor: 'white',
            borderTop: isContentScrollable ? '1px solid #e0e0e0' : 'none',
            p: 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            {buttonFields.map((btnField) => renderField(btnField))}
          </Box>
        </Box>
      )}

      {/* Popup Dialog */}
      {openPopupId && definition?.popups && (() => {
        const popup = definition.popups!.find(p => p.id === openPopupId);
        if (!popup) return null;

        return (
          <Dialog
            open={true}
            onClose={handleClosePopup}
            maxWidth="sm"
            fullWidth
            sx={{ '& .MuiDialog-paper': { borderRadius: '16px', border: '1px solid #3A7380' } }}
          >
            <DialogContent sx={{ p: 4, textAlign: 'center', backgroundColor: '#E5F0F1' }}>
              {popup.content && (
                <Box
                  sx={{ color: '#3A7380', fontFamily: '"Open Sans", sans-serif', mb: 3 }}
                  dangerouslySetInnerHTML={{ __html: popup.content }}
                />
              )}
            </DialogContent>
            <DialogActions
              sx={{
                p: 3, pt: 0,
                justifyContent: popup.buttonLayout === 'space-between' ? 'space-between' : 'center',
                flexDirection: popup.buttonLayout === 'stack' ? 'column' : 'row',
                gap: 2,
                flexWrap: popup.buttonLayout === 'stack' ? 'nowrap' : 'wrap',
                backgroundColor: '#E5F0F1',
              }}
            >
              {popup.buttons
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((button) => (
                  <Button
                    key={button.id}
                    variant="contained"
                    onClick={() => handlePopupButtonClick(button)}
                    sx={{
                      borderRadius: '10px',
                      minWidth: '120px',
                      ...(popup.buttonLayout === 'stack' ? { width: '100%', maxWidth: '300px' } : {}),
                      height: button.buttonHeight === 'small' ? '36px' : button.buttonHeight === 'large' ? '48px' : '40px',
                      fontFamily: '"Open Sans", sans-serif', fontWeight: 700, fontSize: '16px',
                      lineHeight: '100%', letterSpacing: '0%', textAlign: 'center',
                      ...(button.buttonStyle === 'secondary' ? {
                        backgroundColor: '#D7D7D7', color: '#3A7380',
                        '&:hover': { backgroundColor: '#C0C0C0' },
                      } : {
                        backgroundColor: '#3A7380', color: 'white',
                        '&:hover': { backgroundColor: '#2e5c66' },
                      }),
                    }}
                  >
                    {button.label}
                  </Button>
                ))}
            </DialogActions>
          </Dialog>
        );
      })()}
    </>
  );
};

interface QuestionnairePreviewProps {
  open: boolean;
  definition: QuestionnaireDefinitionData;
  title: string;
  onClose: () => void;
}

export const QuestionnairePreview: React.FC<QuestionnairePreviewProps> = ({
  open,
  definition,
  title,
  onClose,
}) => {
  if (!definition || !definition.sections || definition.sections.length === 0) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2, p: 2 } }}
      >
        <DialogContent
          sx={{
            overflowY: 'auto',
            paddingRight: '8px',
            '&::-webkit-scrollbar': { width: '10px' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent', marginTop: '10px', marginBottom: '10px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#3A7380', borderRadius: '10px', border: '2px solid white',
              '&:hover': { backgroundColor: '#2e5c66' },
            },
          }}
        >
          <Typography
            variant="h5"
            align="center"
            sx={{ color: '#2c7a7b', fontWeight: 600, mb: 3, fontFamily: '"Open Sans", sans-serif' }}
          >
            {title}
          </Typography>
          <Typography color="textSecondary" align="center" sx={{ fontFamily: '"Open Sans", sans-serif' }}>
            No sections to preview
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 2, p: 3, maxHeight: '90vh' } }}
    >
      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '70vh' }}
      >
        <QuestionnairePreviewContent key={open ? 'open' : 'closed'} definition={definition} title={title} />
      </DialogContent>
    </Dialog>
  );
};
